import React, { useCallback, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCol,
  MDBContainer,
  MDBIcon,
  MDBRow,
} from 'mdbreact';

import NavBar from '../../../components/NavBar';
import Loading from '../../../components/Loading';
import api from '../../../services/api';
import resolverMensagemErro from '../../../utils/resolverMensagemErro';
import { dataHora, nomeAgente } from '../Abordagens/Imprimir/relatorio/formatadores';

const LIMITE = 20;

// `data_ref` é DATEONLY ('AAAA-MM-DD'), sem horário — mesmo truque de
// `dataDoCabecalho` em RelatorioDia/index.js: fixa meio-dia em Brasília antes
// de formatar, para o dia impresso nunca escorregar por causa de fuso.
const dataRefFormatada = isoData =>
  isoData ? dataHora(`${isoData}T12:00:00.000-03:00`).split(' ')[0] : '—';

// O IP continua sendo gravado em `relatorio_dia_emissao.ip`, mas NÃO é exibido
// aqui: quem lê esta tela é coordenação de posto, não TI — o endereço não
// responde nenhuma pergunta que ela tenha. Hoje o valor seria enganoso de todo
// jeito: sem `trust proxy` no Express, `req.ip` devolve o endereço do gateway,
// igual para todo mundo. O dado fica no banco para uma investigação formal,
// quando e se isso for corrigido.

// Mesma fonte de verdade das outras telas (styles/cores-sobrias.css), como
// em pages/Lotacao — nada de hex solto aqui.
const COR_DESTRUTIVA = 'var(--sig-destrutiva)';

// 48px de alvo: a trilha também é consultada no celular, de pé. Vale para os
// campos (`form-control`/`react-select`) E para os botões outline (Filtrar,
// Limpar filtros, Anterior, Próxima) — sem `style` explícito eles nasciam com
// os ~46px do `.btn` do MDB, uma inconsistência sutil ao lado dos campos.
const ALTURA_CAMPO = 48;
const ESTILO_SELECT = {
  control: base => ({ ...base, minHeight: ALTURA_CAMPO }),
};

// A etiqueta é desenhada aqui, e não com `.badge` do MDB: lá o `color: #fff`
// vem com `!important` e o par claro (fundo cinza) ficava branco no branco.
const ESTILO_ETIQUETA = {
  display: 'inline-block',
  padding: '0.25rem 0.6rem',
  borderRadius: '10rem',
  fontSize: '0.75rem',
  fontWeight: 700,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

// A abrangência é o dado que muda a leitura da linha: uma emissão consolidada
// é um arquivo com o efetivo de vários postos dentro. Como etiqueta ela salta
// na varredura; como texto vermelho no meio de seis pares rótulo/valor iguais,
// não saltava.
//
// O rótulo diz "posto e subordinados" e NÃO "todos os postos": desde que a
// trilha passou a respeitar o escopo, o consolidado alcança só o que quem
// emitiu enxerga. Para um subcoordenador de posto folha — que é a maioria de
// quem abre esta tela —, "todos os postos" seria mentira: a emissão dele traz
// exatamente um posto, o dele. Numa tela de auditoria o rótulo tem de
// descrever o alcance real, não o parâmetro que foi enviado.
const Abrangencia = ({ todos }) => (
  <span
    style={
      todos
        ? { ...ESTILO_ETIQUETA, background: COR_DESTRUTIVA, color: '#fff' }
        : { ...ESTILO_ETIQUETA, background: '#e3e8ec', color: '#42565f' }
    }
    title={
      todos
        ? 'Emissão consolidada: o posto de quem emitiu e os postos subordinados a ele'
        : 'Emissão de um único posto'
    }
  >
    {todos ? 'Posto e subordinados' : 'Um posto'}
  </span>
);

/**
 * Trilha de auditoria do "Relatório do dia": quem emitiu, quando, de qual
 * posto, de qual dia, quantas abordagens saíram no documento e se foi a
 * emissão de UM posto só ou o CONSOLIDADO de vários (coluna Abrangência —
 * a diferença entre um posto e ~300 CPFs de cinco postos no mesmo arquivo).
 *
 * Tela só de leitura, restrita a quem tem a funcionalidade concedida
 * (coordenação); o próprio backend barra o resto com 403 (gateRelatorioDia.js).
 *
 * Consome o contrato NORMALIZADO de `GET /relatorio-dia-emissoes`
 * (`emitido_em`, `emissor`, `posto`) — o Sequelize devolvia `createdAt`
 * (não `created_at`) e um alias de gerência quebrado por inflection
 * (`gerencium`), e a tela ficou mostrando "—" em Emitido em/Posto em
 * produção até o controller passar a mapear a resposta explicitamente.
 */
export default function RelatorioDiaEmissoes() {
  const [linhas, setLinhas] = useState([]);
  const [qtdTotal, setQtdTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  const [listaGerencias, setListaGerencias] = useState([]);
  const [gerenciaSelecionada, setGerenciaSelecionada] = useState(null);

  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // No celular a tela abre com o filtro FECHADO: quem chega aqui vem LER a
  // trilha, e o painel aberto empurrava a primeira emissão para fora da tela.
  // No desktop ele é sempre visível (`d-md-block`) — lá o espaço sobra.
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  // Não existe endpoint de busca de usuários restrito a quem tem acesso a
  // esta trilha (só 13 pessoas) — em vez de inventar back-end novo ou listar
  // todo mundo do sistema, o filtro de emissor cresce com quem já apareceu
  // nos resultados carregados (acumula entre trocas de filtro, nunca some).
  // Por isso ele vem com um aviso: "não achei fulano" pode só significar
  // "ainda não carreguei a página dele".
  const [emissores, setEmissores] = useState({});
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);

  // Guarda contra corrida entre requisições: trocar filtro rápido pode fazer
  // a resposta de uma busca ANTERIOR chegar depois da mais recente. Sem isto
  // a tela de auditoria mostraria dado errado em silêncio (resposta antiga
  // sobrescrevendo a nova). Só a última chamada em voo tem permissão de
  // gravar estado.
  const requisicaoAtual = useRef(0);

  const carregaGerencias = useCallback(() => {
    api
      .get('/filtra-gerencia')
      .then(resposta => {
        // devolve inativas também — não faz sentido filtrar emissões por um
        // posto desativado (mesmo filtro de RelatorioDia/index.js).
        const ativas = (resposta.data || []).filter(g => g.ativo !== false);
        setListaGerencias(
          ativas.map(g => ({ label: g.sigla || g.nome, value: String(g.id) }))
        );
      })
      .catch(() => {
        toast.error('Não foi possível carregar a lista de postos.');
      });
  }, []);

  const buscaEmissoes = useCallback(async () => {
    const idRequisicao = ++requisicaoAtual.current;

    const params = { page, limit: LIMITE };
    if (gerenciaSelecionada) params.gerencia_id = gerenciaSelecionada.value;
    if (usuarioSelecionado) params.usuario_id = usuarioSelecionado.value;
    // o backend só aplica o filtro de período quando os dois vêm preenchidos.
    if (dataInicio && dataFim) {
      params.data_inicio = dataInicio;
      params.data_fim = dataFim;
    }

    setLoading(true);
    try {
      const resultado = await api.get('/relatorio-dia-emissoes', { params });
      if (idRequisicao !== requisicaoAtual.current) return; // resposta velha: descarta

      setLinhas(resultado.data.rows);
      setQtdTotal(resultado.data.count);
      setErro(null);
      setEmissores(anterior => {
        const novo = { ...anterior };
        resultado.data.rows.forEach(linha => {
          if (linha.emissor) novo[linha.emissor.id] = nomeAgente(linha.emissor);
        });
        return novo;
      });
    } catch (err) {
      if (idRequisicao !== requisicaoAtual.current) return; // resposta velha: descarta

      // in-page, e não só toast: um toast some sozinho e a lista ANTERIOR
      // ficava na tela como se fosse o resultado do filtro novo — numa
      // trilha de auditoria isso é ler dado errado sem perceber.
      const mensagem = resolverMensagemErro(err);
      setErro(mensagem);
      setLinhas([]);
      setQtdTotal(0);
      toast.error(mensagem);
    } finally {
      if (idRequisicao === requisicaoAtual.current) setLoading(false);
    }
  }, [page, gerenciaSelecionada, usuarioSelecionado, dataInicio, dataFim]);

  useEffect(() => {
    carregaGerencias();
  }, [carregaGerencias]);

  useEffect(() => {
    buscaEmissoes();
  }, [buscaEmissoes]);

  const opcoesUsuario = Object.entries(emissores)
    .map(([id, label]) => ({ value: id, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

  const totalPaginas = Math.max(1, Math.ceil(qtdTotal / LIMITE));

  // o período só conta como filtro quando o par está completo — é assim que o
  // backend o aplica, e dizer "1 filtro" com só uma ponta preenchida seria
  // prometer um recorte que não aconteceu.
  const filtrosAtivos = [
    gerenciaSelecionada,
    usuarioSelecionado,
    dataInicio && dataFim,
  ].filter(Boolean).length;

  const limpaFiltros = () => {
    setGerenciaSelecionada(null);
    setUsuarioSelecionado(null);
    setDataInicio('');
    setDataFim('');
    setPage(1);
  };

  // trocar qualquer filtro volta pra página 1 — senão a paginação antiga fica
  // fora do novo total e some com linhas que deveriam aparecer.
  const selecionaGerencia = valor => {
    setGerenciaSelecionada(valor);
    setPage(1);
  };
  const selecionaUsuario = valor => {
    setUsuarioSelecionado(valor);
    setPage(1);
  };
  const alteraDataInicio = valor => {
    setDataInicio(valor);
    setPage(1);
  };
  const alteraDataFim = valor => {
    setDataFim(valor);
    setPage(1);
  };

  return (
    <NavBar>
      <MDBContainer className="p-0">
        <MDBRow className="h4-responsive" center>
          Emissões do relatório do dia
        </MDBRow>

        {/* Botão de filtro só do celular: diz quantos filtros estão valendo,
            para o painel fechado nunca esconder um recorte ativo. */}
        <div className="d-md-none mx-2 mb-2">
          <MDBBtn
            outline
            color="mdb-color"
            className="btn-block m-0"
            style={{ height: ALTURA_CAMPO }}
            onClick={() => setFiltrosAbertos(!filtrosAbertos)}
          >
            <MDBIcon icon="filter" className="mr-2" />
            {filtrosAbertos
              ? 'Fechar filtros'
              : `Filtrar${filtrosAtivos ? ` (${filtrosAtivos})` : ''}`}
          </MDBBtn>
        </div>

        <MDBCard
          className={`mx-2 mx-md-3 mb-3 ${filtrosAbertos ? '' : 'd-none d-md-block'}`}
        >
          <MDBCardBody>
            <MDBRow center>
              <MDBCol size="12" md="3" className="mb-3 mb-md-0">
                <label className="mb-1 small text-muted d-block" htmlFor="posto-emissoes">
                  Posto
                </label>
                <Select
                  inputId="posto-emissoes"
                  placeholder="Todos"
                  options={listaGerencias}
                  onChange={selecionaGerencia}
                  value={gerenciaSelecionada}
                  styles={ESTILO_SELECT}
                  isClearable
                />
              </MDBCol>
              <MDBCol size="12" md="3" className="mb-3 mb-md-0">
                <label className="mb-1 small text-muted d-block" htmlFor="emissor-emissoes">
                  Emissor
                </label>
                <Select
                  inputId="emissor-emissoes"
                  placeholder="Todos"
                  options={opcoesUsuario}
                  onChange={selecionaUsuario}
                  value={usuarioSelecionado}
                  styles={ESTILO_SELECT}
                  isClearable
                />
                <div className="small text-muted mt-1">
                  Lista cresce conforme mais páginas são carregadas — não
                  achar alguém aqui não significa que não emitiu.
                </div>
              </MDBCol>
              <MDBCol size="6" md="3">
                <label className="mb-1 small text-muted d-block" htmlFor="data-inicio-emissoes">
                  Data início
                </label>
                <input
                  id="data-inicio-emissoes"
                  className="w-100 form-control"
                  style={{ height: ALTURA_CAMPO }}
                  type="date"
                  value={dataInicio}
                  max={dataFim || undefined}
                  onChange={e => alteraDataInicio(e.target.value)}
                />
              </MDBCol>
              <MDBCol size="6" md="3">
                <label className="mb-1 small text-muted d-block" htmlFor="data-fim-emissoes">
                  Data fim
                </label>
                <input
                  id="data-fim-emissoes"
                  className="w-100 form-control"
                  style={{ height: ALTURA_CAMPO }}
                  type="date"
                  value={dataFim}
                  min={dataInicio || undefined}
                  onChange={e => alteraDataFim(e.target.value)}
                />
              </MDBCol>
            </MDBRow>

            {/* limpar tudo de uma vez: com quatro controles, desfazer um
                recorte à mão é o que mais custa tempo aqui. */}
            {filtrosAtivos > 0 && (
              <MDBRow className="mt-3">
                <MDBCol size="12" md="3">
                  <MDBBtn
                    outline
                    color="mdb-color"
                    className="btn-block m-0"
                    style={{ height: ALTURA_CAMPO }}
                    onClick={limpaFiltros}
                  >
                    <MDBIcon icon="times" className="mr-2" />
                    Limpar filtros
                  </MDBBtn>
                </MDBCol>
              </MDBRow>
            )}
          </MDBCardBody>
        </MDBCard>

        {!erro && (
          <MDBRow
            style={{ borderRadius: '5px' }}
            className="p-2 mr-1 ml-1 mt-1 mr-md-3 ml-md-3 align-items-center grey darken-1 d-none d-md-flex text-white font-weight-bolder"
          >
            <MDBCol size="12" md="2" className="text-center">
              Emitido em
            </MDBCol>
            <MDBCol size="12" md="3" className="text-center">
              Emissor
            </MDBCol>
            <MDBCol size="12" md="2" className="text-center">
              Posto
            </MDBCol>
            <MDBCol size="12" md="2" className="text-center">
              Abrangência
            </MDBCol>
            <MDBCol size="12" md="2" className="text-center">
              Dia do relatório
            </MDBCol>
            <MDBCol size="12" md="1" className="text-center">
              Abordagens
            </MDBCol>
          </MDBRow>
        )}

        {loading && <Loading />}

        {!loading && erro && (
          <MDBRow center className="my-5">
            <MDBCol size="12" md="8" className="text-center">
              <p className="font-weight-bolder text-danger">{erro}</p>
              <MDBBtn color="mdb-color" size="sm" onClick={buscaEmissoes}>
                Tentar de novo
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        )}

        {!loading && !erro && linhas.length === 0 && (
          <MDBRow center className="my-4 text-muted">
            Nenhuma emissão no período
          </MDBRow>
        )}

        {!loading &&
          !erro &&
          linhas.map(linha => (
            <MDBRow
              key={linha.id}
              style={{ borderRadius: '5px' }}
              className="p-2 mr-1 ml-1 mt-1 mr-md-3 ml-md-3 align-items-center bg-white"
            >
              <MDBCol
                size="12"
                md="2"
                className="d-flex justify-content-between justify-content-md-center"
              >
                <span className="d-sm-inline d-md-none mr-3 text-muted">Emitido em</span>
                <span>{dataHora(linha.emitido_em)}</span>
              </MDBCol>
              <MDBCol
                size="12"
                md="3"
                className="d-flex justify-content-between justify-content-md-center"
              >
                <span className="d-sm-inline d-md-none mr-3 text-muted">Emissor</span>
                <span className="font-weight-bolder text-right">
                  {nomeAgente(linha.emissor)}
                </span>
              </MDBCol>
              <MDBCol
                size="12"
                md="2"
                className="d-flex justify-content-between justify-content-md-center"
              >
                <span className="d-sm-inline d-md-none mr-3 text-muted">Posto</span>
                <span>{(linha.posto && linha.posto.sigla) || '—'}</span>
              </MDBCol>
              <MDBCol
                size="12"
                md="2"
                className="d-flex justify-content-between justify-content-md-center"
              >
                <span className="d-sm-inline d-md-none mr-3 text-muted">Abrangência</span>
                <Abrangencia todos={linha.abrangencia === 'todos'} />
              </MDBCol>
              <MDBCol
                size="12"
                md="2"
                className="d-flex justify-content-between justify-content-md-center"
              >
                <span className="d-sm-inline d-md-none mr-3 text-muted">
                  Dia do relatório
                </span>
                <span>{dataRefFormatada(linha.data_ref)}</span>
              </MDBCol>
              <MDBCol
                size="12"
                md="1"
                className="d-flex justify-content-between justify-content-md-center"
              >
                <span className="d-sm-inline d-md-none mr-3 text-muted">Abordagens</span>
                <span>{linha.qtd_abordagens}</span>
              </MDBCol>
            </MDBRow>
          ))}

        {/* Paginação em botões de verdade, não em ícones soltos: os ícones
            não tinham área de toque nem estado desabilitado — no celular
            dava para tocar em "próxima" na última página e nada acontecer.
            A contagem total responde a pergunta que a trilha faz primeiro:
            quantas emissões existem neste recorte. */}
        {!loading && !erro && (
          <MDBRow center className="my-4 align-items-center mx-1 mx-md-3">
            <MDBCol size="4" md="auto" className="px-1">
              <MDBBtn
                outline
                color="mdb-color"
                className="btn-block m-0 px-2"
                style={{ height: ALTURA_CAMPO }}
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                <MDBIcon icon="angle-left" className="mr-1" />
                Anterior
              </MDBBtn>
            </MDBCol>
            <MDBCol size="4" md="auto" className="px-1 text-center">
              <div className="font-weight-bolder">
                {page} / {totalPaginas}
              </div>
              <div className="small text-muted">
                {qtdTotal} {qtdTotal === 1 ? 'emissão' : 'emissões'}
              </div>
            </MDBCol>
            <MDBCol size="4" md="auto" className="px-1">
              <MDBBtn
                outline
                color="mdb-color"
                className="btn-block m-0 px-2"
                style={{ height: ALTURA_CAMPO }}
                disabled={page >= totalPaginas}
                onClick={() => setPage(page + 1)}
              >
                Próxima
                <MDBIcon icon="angle-right" className="ml-1" />
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        )}
      </MDBContainer>
    </NavBar>
  );
}
